/**
 * Mission-ended score-tally sound effects.
 *
 * Three samples that score the end-of-mission count-up on MissionEndedScreen:
 *   • Counter loop — runs continuously while the total ticks upward.
 *   • Category     — a one-shot as each scored category locks in its final value.
 *   • Total        — a one-shot when the grand total lands (and the grade reveals).
 *
 * Like uiSound.js, these live OUTSIDE the per-scenario AudioManager (which is
 * bound to the 3D listener + engine and is torn down / rebuilt between
 * scenarios): the score sheet is a menu screen that exists outside any scenario,
 * so plain HTMLAudioElements suffice. Playback is gated behind the user gesture
 * that reaches this screen, so it sidesteps the browser autoplay policy.
 *
 * One-shots (category / total) clone a template per play so overlapping triggers
 * each get their own voice. The counter loop is a single persistent voice that
 * start/stopScoreCounter() toggle.
 */

import { audioUrl } from '../audio/soundManifest.js';

const BASE = 'assets/sound/';
const CATEGORY_SRC = audioUrl(BASE + 'FinalScore_Category.wav');
const COUNTER_SRC  = audioUrl(BASE + 'FinalScore_Counter_Loop.wav');
const TOTAL_SRC    = audioUrl(BASE + 'FinalScore_Total.wav');

const CATEGORY_VOLUME = 0.5;
const COUNTER_VOLUME  = 0.4;
const TOTAL_VOLUME    = 0.6;

let categoryTemplate = null; // one-shot template, cloned per category lock
let totalTemplate = null;    // one-shot template, cloned for the final flourish
let counterVoice = null;     // single looping voice toggled by start/stop

/** Preload the samples. Safe to call multiple times. */
export function initMissionEndedSound() {
  if (categoryTemplate) return;

  categoryTemplate = new Audio(CATEGORY_SRC);
  categoryTemplate.preload = 'auto';
  categoryTemplate.volume = CATEGORY_VOLUME;

  totalTemplate = new Audio(TOTAL_SRC);
  totalTemplate.preload = 'auto';
  totalTemplate.volume = TOTAL_VOLUME;

  counterVoice = new Audio(COUNTER_SRC);
  counterVoice.preload = 'auto';
  counterVoice.loop = true;
  counterVoice.volume = COUNTER_VOLUME;
}

/** Start the looping counter sound from the top. */
export function startScoreCounter() {
  if (!counterVoice) initMissionEndedSound();
  try { counterVoice.currentTime = 0; } catch { /* not seekable yet — ignore */ }
  counterVoice.play().catch(() => { /* autoplay blocked / interrupted — ignore */ });
}

/** Stop the looping counter sound and rewind it for the next run. */
export function stopScoreCounter() {
  if (!counterVoice) return;
  counterVoice.pause();
  try { counterVoice.currentTime = 0; } catch { /* not seekable yet — ignore */ }
}

/** Play a one-shot as a score category locks in. Overlapping locks each voice. */
export function playCategoryLock() {
  if (!categoryTemplate) initMissionEndedSound();
  const voice = categoryTemplate.cloneNode();
  voice.volume = CATEGORY_VOLUME;
  voice.play().catch(() => { /* autoplay blocked / interrupted — ignore */ });
}

/** Play the one-shot total flourish when the grand total lands. */
export function playScoreTotal() {
  if (!totalTemplate) initMissionEndedSound();
  const voice = totalTemplate.cloneNode();
  voice.volume = TOTAL_VOLUME;
  voice.play().catch(() => { /* autoplay blocked / interrupted — ignore */ });
}
